import React, { useState, useEffect } from 'react';
import { Table, Select, Tag, Modal } from 'antd';
import {
  FilterFilled,
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import { getData, postData } from '../../config/api-connect';
import { getColumnSearchProps, dateSorter, getColumnFilters } from '../../utils/filters';
import SummaryLayout from '../../components/layout/SummaryLayout';
import './subscriptionLogs.css';
import DetailsDrawer from '../../components/ui/DetailsDrawer';
import MonthSelector from '../../components/ui/MonthSelector';
import { useMonthFilter } from '../../hooks/useMonthFilter';
import { useCityName } from '../../hooks/useCityName';

function SubscriptionChangeLogs() {
  const { Option } = Select;

  const { selectedMonth, setSelectedMonth } = useMonthFilter();
  const { cityName: city_name, setCityName } = useCityName();

  const [logsLoader, setLogsLoader] = useState(false);
  const [changeLogs, setChangeLogs] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedTableRow, setSelectedTableRow] = useState({});

  const cityOptions = [
    'Bangalore',
    'Kerala',
    'Hyderabad',
    'Qatar',
    'UAE',
    'Kuwait',
    'Poland',
    'France',
    'Germany',
    'Thailand',
  ];

  useEffect(() => {
    get_change_logs();
  }, [city_name, selectedMonth.format('YYYY-MM')]);

  const get_change_logs = () => {
    setLogsLoader(true);

    const start_date = selectedMonth.clone().startOf('month').format('YYYY-MM-DD');
    const end_date = selectedMonth.clone().endOf('month').format('YYYY-MM-DD');

    getData(
      `/finance/get_venuewise_edit_logs/?city_name=${city_name}&start_date=${start_date}&end_date=${end_date}`,
    )
      .then((response) => {
        setChangeLogs(response.result);
        setSummaryData(response.summary || []);
        setLogsLoader(false);
      })
      .catch((error) => {
        setLogsLoader(false);
        alert(error.message);
      });
  };
  const confirmVerify = (record) => {
    const collection_id = record?.collection_id || record?.id;
    if (!collection_id) return Modal.error({ content: 'collection_id missing' });

    Modal.confirm({
      title: 'Confirm',
      content: 'Are you sure to mark this payment as verified ?',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        updateVerificationTag(collection_id);
      },
    });
  };

  const updateVerificationTag = async (collection_id) => {
    try {
      const response = await postData('/finance/verify_collection/', { collection_id });
      if (response.success) {
        setChangeLogs((prev) =>
          prev.map((item) =>
            item.collection_id === collection_id || item.id === collection_id
              ? { ...item, is_verified_collection: true }
              : item,
          ),
        );
        setSelectedTableRow((prev) =>
          prev && (prev.collection_id === collection_id || prev.id === collection_id)
            ? { ...prev, is_verified_collection: true }
            : prev,
        );
        Modal.success({ content: response.message });
      } else {
        Modal.error({ content: response.message });
      }
    } catch (error) {
      Modal.error({ content: error.message });
    }
  };
  const uniqueValues = (key) =>
    Array.from(new Set(changeLogs.map((r) => r[key]).filter(Boolean))).sort();

  const columns = [
    {
      title: 'Verified',
      dataIndex: 'is_verified_collection',
      align: 'center',
      render: (val) =>
        val ? (
          <CheckCircleOutlined className="sub-logs-check-icon" />
        ) : (
          <CloseCircleOutlined className="sub-logs-close-icon" />
        ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      filterMultiple: true,
      onFilter: (value, record) => Boolean(record.is_verified_collection) === Boolean(value),
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      filterIcon: (filtered) => <FilterFilled className="sub-logs-filter-icon" />,
    },
    {
      title: 'Edited Date',
      dataIndex: 'edited_date',
      align: 'center',
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      align: 'left',
      ...getColumnSearchProps('customer'),
      onFilter: (value, record) => {
        const name = record?.customer ? record.customer.toString().toLowerCase() : '';
        const mobile = record?.mobile_no ? record.mobile_no.toString().toLowerCase() : '';
        return name.includes(value.toLowerCase()) || mobile.includes(value.toLowerCase());
      },
      render: (text, record) => (
        <>
          <div>{text ?? <span className="sub-logs-text-muted">—</span>}</div>
          <div className="sub-logs-subtext">{record?.mobile_no ?? ''}</div>
        </>
      ),
    },

    {
      title: 'Venue',
      dataIndex: 'venue',
      align: 'center',
      width: 150,
      ...getColumnFilters('venue', uniqueValues('venue')),
    },
    {
      title: 'Subscription Dates',
      dataIndex: 'subscription_start_date',
      align: 'center',
      sorter: dateSorter('subscription_start_date'),
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (text, record) => {
        const start = text;
        const end = record?.subscription_end_date;
        if (!start && !end) return <span className="sub-logs-text-muted">—</span>;
        return (
          <>
            <div>{start ? `${start} →` : <span className="sub-logs-text-muted">—</span>}</div>
            <div className="sub-logs-subtext">{end ?? ''}</div>
          </>
        );
      },
    },
    {
      title: 'Change Type',
      dataIndex: 'change_type',
      render: (value) => value ?? <span className="sub-logs-text-muted">—</span>,
      align: 'center',
      ...getColumnFilters('change_type', uniqueValues('change_type')),
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
    },
    {
      title: 'Changes',
      dataIndex: 'changes',
      align: 'center',
      width: 200,
    },
    {
      title: 'Changed By',
      dataIndex: 'changed_by',
      render: (value) => value ?? <span className="sub-logs-text-muted">—</span>,
      align: 'center',
      ...getColumnFilters('changed_by', uniqueValues('changed_by')),
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
    },
    {
      title: 'Package',
      dataIndex: 'package_name',
      align: 'center',
      ...getColumnSearchProps('package_name'),
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
    },
    {
      title: 'Amount',
      dataIndex: 'collection_amount',
      align: 'right',
      width: 140,
      sorter: (a, b) => (Number(a.collection_amount) || 0) - (Number(b.collection_amount) || 0),
      render: (val) =>
        val == null ? (
          <span className="sub-logs-text-muted">—</span>
        ) : (
          Number(val).toLocaleString('en-IN')
        ),
    },
    {
      title: 'Payment Mode',
      dataIndex: 'payment_mode',
      align: 'center',
      onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      ...getColumnFilters('payment_mode', uniqueValues('payment_mode')),
    },
    {
      title: '',
      dataIndex: 'more',
      key: 'more',
      fixed: 'right',
      render: (item, record) => (
        <a className="sub-logs-arrow-link" onClick={() => clickedARow(record)}>
          <ArrowRightOutlined className="sub-logs-arrow-icon" />
        </a>
      ),
    },
  ];

  const drawerFields = [
    { label: 'Customer', key: 'customer' },
    { label: 'Mobile No', key: 'mobile_no' },
    { label: 'Venue', key: 'venue' },
    { label: 'Start Date', key: 'subscription_start_date' },
    { label: 'End Date', key: 'subscription_end_date' },
    { label: 'Package', key: 'package_name' },
    { label: 'Payment Mode', key: 'payment_mode' },
    {
      label: 'Verified',
      key: 'is_verified_collection',
      render: (d) =>
        d?.is_verified_collection ? (
          <Tag color="green">VERIFIED</Tag>
        ) : d?.collection_id ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              confirmVerify(d);
            }}
            className="sub-logs-clickable"
          >
            <Tag color="orange">NOT VERIFIED</Tag>
            <div className="sub-logs-verify-hint">
              (Click to verify)
            </div>
          </div>
        ) : (
          <Tag color="orange">NOT VERIFIED</Tag>
        ),
    },
    {
      label: 'Amount',
      key: 'collection_amount',
      render: (d) =>
        d?.collection_amount == null ? '—' : Number(d.collection_amount).toLocaleString(),
    },
    { label: 'Edited Date', key: 'edited_date' },
    { label: 'Edit Type', key: 'change_type' },
    { label: 'Edited By', key: 'changed_by' },
    { label: 'Changes', key: 'changes' },
    { label: 'Reason', key: 'reason' },
    { label: 'Concerned Person', key: 'concerned_person' },
  ];

  const clickedARow = (row) => {
    setSelectedTableRow(row);
    setOpenDrawer(true);
  };

  const renderRightSideActions = () => (
    <>
      <Select
        placeholder="Select City"
        className="sub-logs-select"
        value={city_name}
        onChange={(value) => setCityName(value)}
      >
        {cityOptions.map((city) => (
          <Option key={city} value={city}>
            {city}
          </Option>
        ))}
      </Select>

      <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
    </>
  );

  const statsConfig = (summaryData || []).map((s, i) => ({ key: `s_${i}`, title: s.label }));
  const totals = (summaryData || []).reduce((acc, s, i) => ({ ...acc, [`s_${i}`]: s.count }), {});

  return (
    <>
      <SummaryLayout
        title="Subscription Edit Logs"
        headerContent={renderRightSideActions()}
        statsConfig={statsConfig}
        totals={totals}
        statsLoading={logsLoader}
        tableProps={{
          rowKey: 'id',
          columns,
          data: changeLogs,
          loading: logsLoader,
          pagination: { pageSize: 20, showSizeChanger: false },
          onRow: (record) => ({
            onClick: () => clickedARow(record),
            style: { cursor: 'pointer' },
          }),
        }}
      />

      <DetailsDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        title="Log Details"
        data={selectedTableRow}
        fields={drawerFields}
      />
    </>
  );
}

export default SubscriptionChangeLogs;
